import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import useAsyncEffect from 'use-async-effect';
import Axios from 'axios';

import Api from '../../libs/Api';

import { ControlPanelStyle } from './ControlPanelStyle';

import PageWithMenu from '../../components/PageWithMenu/PageWithMenu';
import Header from '../../components/Header/Header';
import InputField from '../../components/InputField/InputField';
import SelectOptions from '../../components/SelectOptions/SelectOptions';
import Button from '../../components/Button/Button';
import AddUserForm from '../../components/AddUserForm/AddUserForm';
import UsersList from '../../components/UsersList/UsersList';
import SnackBar from '../../components/SnackBar/SnackBar';
import translate from '../../libs/i18n';

import Loading from '../../components/Loading/Loading';

import SearchIcon from '../../assets/svgs/SearchIcon';
import AddIcon from '../../assets/svgs/AddIcon';

import { breakpoints } from '../../ui/variables';
import { useBreakpoint } from '../../utils/useBreakpoint';
import { headerVariables } from '../../components/Header/HeaderStyle';
import { useIsMounted } from '../../utils/useIsMounted';

const ControlPanel = () => {
  const breakpoint = useBreakpoint();
  const isMounted = useIsMounted();
  const institution = useSelector((state) => state.settings.institution);
  const language = useSelector((state) => state.settings.language);
  const [showMenu, setShowMenu] = useState(false);

  const [pageWithMenuStyle] = useState({
    paddingTop: `${headerVariables.main.height.iphone + 52}px`,
  });
  const [userSearch, setUserSearch] = useState('');
  const [isOpenAddUserForm, setIsOpenAddUserForm] = useState('');
  const [users, setUsers] = useState([]);
  const [userOrderType, setUserOrderType] = useState('');
  const [filterByUserType, setFilterByUserType] = useState('');
  const [deleteUser, setDeleteUser] = useState(false);
  const [editUserData, setEditUserData] = useState('');
  const [userActionComplete, setUserActionComplete] = useState(null);

  const [institutionPermissions, setInstitutionPermissions] = useState();

  const [loading, setLoading] = useState(true);
  const [totalOfUsers, setTotalOfUsers] = useState(0);

  useAsyncEffect(async () => {
    if (institution?.id) {
      const getExamResume = Api.getExamResume();
      const getUserByInstitution = Api.getUserByInstitution(institution?.id, {
        type: 'DOC,TEC',
      });

      if (!isMounted()) {
        return;
      }
      // let currentExamSummary = [];
      let userList = [];
      let totalUsers = 0;

      if (institution?.id) {
        setLoading(true);

        await Axios.all([getExamResume, getUserByInstitution]).then(
          (response) => {
            userList = response[1]?.data || [];
            totalUsers = userList.length;
          }
        );

        if (isMounted()) {
          setLoading(false);
          setUsers(userList);
          setTotalOfUsers(totalUsers);
        }
      }
    }
  }, [deleteUser, isMounted, institution]);

  useEffect(() => {
    const permissions = [];

    Api.getPermissions().then((response) => {
      if (response && response.data && response.data.length) {
        const responsePermissions = response.data;

        responsePermissions.forEach((permission) => {
          if (permission?.institution === institution?.id) {
            permissions.push(permission);
          }
        });
      }

      setInstitutionPermissions(permissions);
    });
  }, [institution]);

  useEffect(() => {
    (async () => {
      if (!isMounted()) {
        return;
      }

      setLoading(true);

      if (institution?.id) {
        const query = {};
        let currentUsers = [];

        if (filterByUserType?.length) {
          query.type = filterByUserType;
        } else {
          query.type = 'DOC,TEC';
        }

        if (userSearch?.length) {
          query.search = userSearch;
        }

        if (userOrderType?.length) {
          query.order = userOrderType;
        }

        const response = await Api.getUserByInstitution(institution?.id, query);

        if (response?.status === 200) {
          currentUsers = response?.data || [];
        }

        setUsers(currentUsers);
      }

      setLoading(false);
    })();
  }, [filterByUserType, institution?.id, isMounted, userOrderType, userSearch]);

  const hasUserActionCompleteSuccess = useMemo(
    () =>
      userActionComplete &&
      userActionComplete.type &&
      userActionComplete.type === 'success',
    [userActionComplete]
  );

  const hasUserActionCompleteError = useMemo(
    () =>
      userActionComplete &&
      userActionComplete.type &&
      userActionComplete.type === 'error',
    [userActionComplete]
  );

  const hasUserActionCompleteWarning = useMemo(
    () => hasUserActionCompleteSuccess || hasUserActionCompleteError,
    [hasUserActionCompleteSuccess, hasUserActionCompleteError]
  );

  useEffect(() => {
    if (hasUserActionCompleteWarning) {
      const timeout = setTimeout(() => {
        setUserActionComplete(null);
      }, 3500);

      return () => clearTimeout(timeout);
    }

    return null;
  }, [
    hasUserActionCompleteSuccess,
    hasUserActionCompleteError,
    hasUserActionCompleteWarning,
  ]);

  const handleOpenAddUserForm = () => {
    setIsOpenAddUserForm(' opened');
  };

  const handleDeleteUser = (id) => {
    Api.deleteUser(id).then((response) => {
      if (response.status) {
        setDeleteUser(!deleteUser);
      }
    });
  };

  const handleCatchUserDataForEdition = (id) => {
    Api.getUser(id).then((response) => {
      setEditUserData(response);
    });
    setIsOpenAddUserForm(' opened');
  };

  const handleChangeSelect = (value, options, setValue) => {
    const currentOption = options.find(
      (option) => `${option.value}` === `${value}`
    );

    if (currentOption) {
      setValue(currentOption.value);
    }
  };

  const handleShowMenu = () => setShowMenu(!showMenu);
  return (
    <PageWithMenu showMenu={showMenu} menuStyle={pageWithMenuStyle}>
      <ControlPanelStyle>
        <Header
          handleShowMenu={
            breakpoint.width < breakpoints.ipadLandscape.width
              ? handleShowMenu
              : null
          }
        />
        {hasUserActionCompleteWarning ? (
          <div className="snack-bar-wrapper">
            <SnackBar
              type={userActionComplete.type}
              text={userActionComplete.text}
            />
          </div>
        ) : null}
        {/* {examSummary ? (
          <div className="exam-summary-wrapper">
            <ExamSummary
              radiographies={examSummary.radiographies || null}
              tomographies={examSummary.tomographies || null}
            />
          </div>
        ) : null} */}

        <div className="header-users-list">
          <span className="title-header">
            <span className="title">{translate('panelControl', language)}</span>
            <span className="number-of-registers">
              <span className="score">Total:</span> {totalOfUsers} registered
            </span>
          </span>
          <div className="button-wrapper">
            <Button
              type="button"
              theme="default"
              icon={<AddIcon />}
              onClick={handleOpenAddUserForm}
              text={translate('addNewRegister', language)}
            />
          </div>
        </div>

        <AddUserForm
          isOpenAddUserForm={isOpenAddUserForm}
          setIsOpenAddUserForm={setIsOpenAddUserForm}
          editUserData={editUserData}
          setUserActionComplete={setUserActionComplete}
          setLoading={setLoading}
          handleEditUserData={setEditUserData}
        />

        <div className="users-list-form">
          <div className="users-list-search-form">
            <InputField
              shape="circle"
              type="text"
              inputName="search-exam"
              value={userSearch}
              icon={<SearchIcon />}
              placeholder={translate('examDescription', language)}
              onChange={setUserSearch}
              noMarginBottom
            />
          </div>

          <div className="select-wrapper">
            <div className="select-container">
              <SelectOptions
                theme="grayish-blue-outline"
                shape="circle"
                id="role"
                options={[
                  {
                    key: 0,
                    value: '',
                    text: translate('sort', language),
                  },
                  {
                    key: 1,
                    value: '-date_joined',
                    text: 'Mais recente',
                  },
                  {
                    key: 2,
                    value: 'date_joined',
                    text: 'Mais antigo',
                  },
                  {
                    key: 3,
                    value: 'name',
                    text: 'Name',
                  },
                  {
                    key: 4,
                    value: 'type',
                    text: 'Role *',
                  },
                ]}
                value={userOrderType}
                onChange={setUserOrderType}
              />
            </div>
            <div className="select-container">
              <SelectOptions
                theme="grayish-blue-outline"
                shape="circle"
                id="role"
                options={[
                  {
                    key: 0,
                    value: '',
                    text: translate('selectUserType', language),
                  },
                  { key: 1, value: 'DOC', text: 'Doctor' },
                  { key: 2, value: 'TEC', text: 'Technician' },
                ]}
                value={filterByUserType}
                onChange={(value) =>
                  handleChangeSelect(
                    value,
                    [
                      {
                        key: 0,
                        value: '',
                        text: translate('selectUserType', language),
                      },
                      { key: 1, value: 'DOC', text: 'Doctor' },
                      { key: 2, value: 'TEC', text: 'Technician' },
                    ],
                    setFilterByUserType
                  )
                }
              />
            </div>
          </div>
        </div>

        {!loading ? (
          <UsersList
            usersList={users}
            handleDeleteUser={handleDeleteUser}
            handleCatchUserDataForEdition={handleCatchUserDataForEdition}
            institutionPermissions={institutionPermissions}
            setUserActionComplete={setUserActionComplete}
            loading={loading}
          />
        ) : null}
      </ControlPanelStyle>
      {loading ? <Loading /> : null}
    </PageWithMenu>
  );
};

export default ControlPanel;
